import { PRODUCT_HISTORY_MONTHS, ROLLING_LENGTH } from '../measure/constants';
import type { NonEmpty } from '../shared/non-empty';
import type { DateRange, MeasurementWindow, WindowMode } from './measurement';

/** Milliseconds in a UTC calendar day. */
export const MS_PER_DAY = 86_400_000;

/** Calendar days from YYYY-MM-DD `from` to `to`, negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY);
}

// ─── Day enumeration ──────────────────────────────────────────────────

/** YYYY-MM-DD minus `days` calendar days (a negative count adds), read as UTC. */
export function minusDays(isoDay: string, days: number): string {
  return new Date(Date.parse(`${isoDay}T00:00:00Z`) - days * MS_PER_DAY).toISOString().slice(0, 10);
}

/** All YYYY-MM-DD dates from range.from to range.to, inclusive. */
export function expandRange(range: DateRange): string[] {
  const out: string[] = [];
  const end = new Date(range.to + 'T00:00:00Z');
  const cur = new Date(range.from + 'T00:00:00Z');
  while (cur <= end) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
}

// ─── Bucket key derivation ────────────────────────────────────────────

/** YYYY-MM-DD → YYYY-MM calendar-month key. Pinned to current surface month series (YYYY-MM). */
export function monthKey(day: string): string {
  return day.slice(0, 7);
}

/**
 * YYYY-MM-DD → ISO-8601 week key (YYYY-WNN).
 *
 * Week 1 is the week containing the first Thursday of the year
 * (ISO 8601). The ISO year may differ from the calendar year at
 * year boundaries (e.g. 2019-12-30 is week 2020-W01).
 */
export function isoWeekKey(day: string): string {
  const d = new Date(day + 'T00:00:00Z');
  const dayNum = d.getUTCDay() || 7; // Mon=1 … Sun=7
  // Shift to Thursday of the same ISO week
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

/**
 * Bucket key for a single day under a given mode.
 * - rolling28: the day itself (each display day is its own key)
 * - monthly: YYYY-MM
 * - weekly: YYYY-WNN (ISO 8601)
 * - total: the literal string 'total'
 */
export function dayBucketKey(mode: WindowMode, day: string): string {
  switch (mode) {
    case 'monthly':
      return monthKey(day);
    case 'weekly':
      return isoWeekKey(day);
    case 'rolling28':
      return day;
    case 'total':
      return 'total';
  }
}

// ─── Month ranges for observation reads ──────────────────────────────

/**
 * The floor every observation consumer applies to its lead-in: `max(need, DECODE_CONTEXT_DAYS)`.
 * With it the first month read is a full month before the window, so the decoder's Apohja
 * list-price correction (`APOHJA_MAX_CORRECTION_DAYS = 21` in observation.ts, which carries
 * `prevLp` forward across days) yields the same result inside the window as a whole-history decode.
 * The Apohja scan also measures a dip's length forward (`scanApohjaIntervals`), so a window ending
 * in the past needs `DECODE_CONTEXT_DAYS` of lead-out after it as well.
 */
export const DECODE_CONTEXT_DAYS = 28;

/** The earliest day any observation read may ask for; earlier lead-ins are clamped to it. */
export const OBSERVATION_HISTORY_START = '2020-01-01';

/**
 * The YYYY-MM months from the month of `from - leadInDays` to the month of
 * `to + leadOutDays`: ascending, contiguous, de-duplicated. Days are YYYY-MM-DD, read as UTC.
 *
 * The start is clamped to `OBSERVATION_HISTORY_START` (or to `to` when `to` precedes it), so a
 * typed early start stays bounded. The lead-out stops at the current UTC day and never moves the
 * end before `to`: a window ending today adds nothing. Throws when `from > to`; callers guard an
 * empty range themselves.
 */
export function monthsForDayRange(
  from: string,
  to: string,
  leadInDays = 0,
  leadOutDays = 0
): NonEmpty<string> {
  if (from > to) {
    throw new Error(`monthsForDayRange: from ${from} is after to ${to}`);
  }
  const floor = to < OBSERVATION_HISTORY_START ? to : OBSERVATION_HISTORY_START;
  const leadIn = minusDays(from, leadInDays);
  const startDay = leadIn < floor ? floor : leadIn;
  const today = new Date().toISOString().slice(0, 10);
  const leadOut = minusDays(to, -leadOutDays);
  const capped = leadOut > today ? today : leadOut;
  const endDay = capped > to ? capped : to;
  const start = new Date(`${startDay}T00:00:00Z`);
  const last = monthKey(endDay);
  const first = monthKey(startDay);
  const rest: string[] = [];
  const cur = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
  for (let ym = monthKey(cur.toISOString().slice(0, 10)); ym <= last; ) {
    rest.push(ym);
    cur.setUTCMonth(cur.getUTCMonth() + 1);
    ym = monthKey(cur.toISOString().slice(0, 10));
  }
  return [first, ...rest];
}

/**
 * The last `PRODUCT_HISTORY_MONTHS` calendar months up to and including the month of `today`
 * (YYYY-MM-DD), ascending, by UTC string arithmetic.
 */
export function productHistoryMonths(today: string): NonEmpty<string> {
  const index = Number(today.slice(0, 4)) * 12 + Number(today.slice(5, 7)) - PRODUCT_HISTORY_MONTHS;
  const ym = `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
  return monthsForDayRange(firstDayOfMonth(ym), today);
}

/** Two month lists hold the same months in the same order; two undefined lists are equal. */
export function sameMonths(
  a: readonly string[] | undefined,
  b: readonly string[] | undefined
): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined || a.length !== b.length) return false;
  return a.every((m, i) => m === b[i]);
}

/** YYYY-MM -> the first day of that month, YYYY-MM-01. */
export function firstDayOfMonth(ym: string): string {
  return `${ym}-01`;
}

// ─── Window range helpers ─────────────────────────────────────────────

/**
 * Extends range.from backwards by ROLLING_LENGTH-1 (27) days so the
 * first display date has a full 28-day buffer. Rolling-28 is exactly
 * ROLLING_LENGTH days — never 56 (ADR-0005).
 */
export function rollingBufferRange(range: DateRange): DateRange {
  const d = new Date(range.from + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() - (ROLLING_LENGTH - 1));
  return { from: d.toISOString().slice(0, 10), to: range.to };
}

/**
 * The date range from which observations must be fetched for a window.
 * For rolling28, extends backwards by ROLLING_LENGTH-1 days.
 * For all other modes, equals window.range.
 */
export function observationSourceRange(window: MeasurementWindow): DateRange {
  if (window.mode === 'rolling28') return rollingBufferRange(window.range);
  return window.range;
}

// ─── Bucket enumeration ───────────────────────────────────────────────

/**
 * Ordered, deduplicated bucket keys for the display range of a window.
 * Keys appear in the order their first contributing day is encountered.
 * For 'total', returns the single key ['total'].
 * For 'rolling28', returns one key per display day (same as expandRange output).
 */
export function bucketKeys(window: MeasurementWindow): readonly string[] {
  if (window.mode === 'total') return ['total'];
  const days = expandRange(window.range);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const d of days) {
    const k = dayBucketKey(window.mode, d);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  }
  return out;
}

/** Maps each display-day bucket key to the calendar days it contains. */
export function groupDaysByBucket(window: MeasurementWindow): Map<string, string[]> {
  const days = expandRange(window.range);
  const out = new Map<string, string[]>();
  for (const d of days) {
    const k = dayBucketKey(window.mode, d);
    const arr = out.get(k);
    if (arr) arr.push(d);
    else out.set(k, [d]);
  }
  return out;
}

// ─── Observation filtering and assignment ─────────────────────────────

/** Retains only observations whose day falls within range (inclusive). */
export function filterObsByRange<T extends { readonly day: string }>(
  obs: readonly T[],
  range: DateRange
): readonly T[] {
  return obs.filter((o) => o.day >= range.from && o.day <= range.to);
}

/**
 * Assigns observations to their bucket(s) under the given window.
 *
 * - monthly / weekly / total: each observation maps to exactly one bucket.
 *   Observations outside window.range are excluded.
 * - rolling28: each observation in the buffered source range contributes
 *   to every display day whose window [day-27, day] includes the obs day.
 *   Returns one entry per display day (same key space as expandRange(window.range)).
 *
 * Observation type T must carry a `day: string` field (YYYY-MM-DD).
 */
export function assignObsToBuckets<T extends { readonly day: string }>(
  obs: readonly T[],
  window: MeasurementWindow
): Map<string, readonly T[]> {
  if (window.mode === 'rolling28') {
    const sourceRange = rollingBufferRange(window.range);
    const inSource = filterObsByRange(obs, sourceRange) as T[];
    const displayDays = expandRange(window.range);
    // An obs on day y belongs to display days [y, y+27]: index arithmetic
    // replaces the O(obs x days) membership scan.
    const originMs = Date.parse(window.range.from + 'T00:00:00Z');
    const idxByDay = new Map<string, number>();
    const buckets: T[][] = displayDays.map(() => []);
    for (const o of inSource) {
      let idx = idxByDay.get(o.day);
      if (idx === undefined) {
        idx = Math.round((Date.parse(o.day + 'T00:00:00Z') - originMs) / MS_PER_DAY);
        idxByDay.set(o.day, idx);
      }
      const last = Math.min(displayDays.length - 1, idx + ROLLING_LENGTH - 1);
      for (let j = Math.max(0, idx); j <= last; j++) buckets[j].push(o);
    }
    return new Map<string, readonly T[]>(displayDays.map((d, i) => [d, buckets[i]]));
  }

  const inRange = filterObsByRange(obs, window.range) as T[];
  const out = new Map<string, T[]>();
  for (const o of inRange) {
    const k = dayBucketKey(window.mode, o.day);
    const arr = out.get(k);
    if (arr) arr.push(o);
    else out.set(k, [o]);
  }
  return out as Map<string, readonly T[]>;
}

// ─── Compare-window derivation ────────────────────────────────────────

/**
 * Derives the comparison window that immediately precedes main.range.
 * The compare window has the same number of calendar days and the same mode,
 * ending the day before main.range.from.
 *
 * Example: main 2026-01-01 → 2026-03-31 (90 days)
 *   → compare 2025-10-03 → 2025-12-31 (90 days).
 */
export function compareWindowFrom(main: MeasurementWindow): MeasurementWindow {
  const length = expandRange(main.range).length;
  const toD = new Date(main.range.from + 'T00:00:00Z');
  toD.setUTCDate(toD.getUTCDate() - 1);
  const fromD = new Date(toD);
  fromD.setUTCDate(fromD.getUTCDate() - (length - 1));
  return {
    mode: main.mode,
    range: {
      from: fromD.toISOString().slice(0, 10),
      to: toD.toISOString().slice(0, 10),
    },
  };
}
